import * as React from 'react';

import { NumberField } from '@/components/ui/NumberField';
import { cn } from '@/lib/utils';

import { inputVariants, getNumberFieldSize } from './input-variants';

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  radius?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  fullWidth?: boolean;
  label?: string;
  error?: boolean;
  helperText?: string;
  showSpinners?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      size: inputSize,
      radius,
      fullWidth = true,
      label,
      error,
      helperText,
      showSpinners,
      ...props
    }: InputProps,
    ref
  ) => {
    // Use MUI NumberField for number inputs
    if (type === 'number') {
      return (
        <NumberField
          ref={ref}
          // @ts-expect-error - TypeScript has issues with size prop inference, but the function correctly maps to valid values
          size={getNumberFieldSize(inputSize)}
          fullWidth={fullWidth}
          error={error ?? false}
          helperText={helperText ?? ''}
          label={label}
          hideLabel={true} // Hide MUI label since forms use separate Label components
          showSpinners={showSpinners}
          className={cn(className)}
          {...(props as Omit<InputProps, 'label'> & { label?: string })}
        />
      );
    }

    // Use regular HTML input for other types
    return (
      <input
        type={type}
        className={cn(
          inputVariants({
            size: (inputSize === 'xl' || inputSize === 'full' ? 'lg' : inputSize) as
              | 'sm'
              | 'md'
              | 'lg',
            radius: radius === 'xl' ? 'lg' : radius,
            fullWidth,
          }),
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };
